import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Search, Plus, Check, RefreshCw, Tv, X, Filter } from 'lucide-react'

interface PlutoTVChannel {
  id: string
  name: string
  slug: string
  number: number
  logo: string
  category: string
  summary: string
  added: boolean
  stream_id?: string
}

interface PlutoTVResponse {
  channels: PlutoTVChannel[]
  total: number
}

async function fetchPlutoTVChannels(category?: string, search?: string): Promise<PlutoTVResponse> {
  const params = new URLSearchParams()
  if (category) params.set('category', category)
  if (search) params.set('search', search)

  const url = `/api/plutotv/channels${params.toString() ? '?' + params.toString() : ''}`
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
  })
  if (!response.ok) {
    throw new Error('Failed to fetch Pluto TV channels')
  }
  return response.json()
}

async function fetchCategories(): Promise<string[]> {
  const response = await fetch('/api/plutotv/categories', {
    headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
  })
  if (!response.ok) {
    throw new Error('Failed to fetch categories')
  }
  const data = await response.json()
  return data.categories || []
}

async function addChannel(channelId: string): Promise<void> {
  const response = await fetch(`/api/plutotv/channels/${channelId}/add`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
  })
  if (!response.ok) {
    const data = await response.json()
    throw new Error(data.error || 'Failed to add channel')
  }
}

async function removeChannel(channelId: string): Promise<void> {
  const response = await fetch(`/api/plutotv/channels/${channelId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
  })
  if (!response.ok) {
    const data = await response.json()
    throw new Error(data.error || 'Failed to remove channel')
  }
}

async function refreshCache(): Promise<void> {
  const response = await fetch('/api/plutotv/refresh', {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
  })
  if (!response.ok) {
    throw new Error('Failed to refresh cache')
  }
}

export default function PlutoTV() {
  const [search, setSearch] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [pendingAction, setPendingAction] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['plutotv-channels'],
    queryFn: () => fetchPlutoTVChannels(),
    staleTime: 5 * 60 * 1000, // 5 min cache
  })

  const { data: categories = [] } = useQuery({
    queryKey: ['plutotv-categories'],
    queryFn: fetchCategories,
    staleTime: 30 * 60 * 1000, // 30 min cache
  })

  const addMutation = useMutation({
    mutationFn: addChannel,
    onMutate: (channelId) => setPendingAction(channelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plutotv-channels'] })
    },
    onSettled: () => setPendingAction(null),
  })

  const removeMutation = useMutation({
    mutationFn: removeChannel,
    onMutate: (channelId) => setPendingAction(channelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plutotv-channels'] })
    },
    onSettled: () => setPendingAction(null),
  })

  const refreshMutation = useMutation({
    mutationFn: refreshCache,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plutotv-channels'] })
      queryClient.invalidateQueries({ queryKey: ['plutotv-categories'] })
    },
  })

  // Filter channels based on search and category (client-side for responsiveness)
  const filteredChannels = useMemo(() => {
    if (!data?.channels) return []

    return data.channels.filter((channel) => {
      // Category filter
      if (selectedCategory && channel.category !== selectedCategory) {
        return false
      }
      // Search filter
      if (search) {
        const searchLower = search.toLowerCase()
        const matchesName = channel.name.toLowerCase().includes(searchLower)
        const matchesCategory = channel.category.toLowerCase().includes(searchLower)
        if (!matchesName && !matchesCategory) {
          return false
        }
      }
      return true
    })
  }, [data?.channels, search, selectedCategory])

  // Group channels by category for display
  const groupedByCategory = useMemo(() => {
    const groups: Record<string, PlutoTVChannel[]> = {}
    for (const channel of filteredChannels) {
      const cat = channel.category || 'Other'
      if (!groups[cat]) {
        groups[cat] = []
      }
      groups[cat].push(channel)
    }
    return groups
  }, [filteredChannels])

  const sortedCategories = useMemo(() => {
    return Object.keys(groupedByCategory).sort()
  }, [groupedByCategory])

  const addedCount = useMemo(() => {
    return data?.channels?.filter((ch) => ch.added).length || 0
  }, [data?.channels])

  if (error) {
    return (
      <div className="p-8 text-center">
        <div className="text-red-500 mb-4">Failed to load Pluto TV channels</div>
        <button
          onClick={() => queryClient.invalidateQueries({ queryKey: ['plutotv-channels'] })}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Pluto TV Channels</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Browse and add free streaming channels from Pluto TV
            {data && (
              <span className="ml-2">
                ({addedCount} added / {data.total} available)
              </span>
            )}
          </p>
        </div>
        <button
          onClick={() => refreshMutation.mutate()}
          disabled={refreshMutation.isPending}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-0 sm:min-w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search channels..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Category Filter */}
          <div className="relative w-full sm:w-auto sm:min-w-48">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white appearance-none"
            >
              <option value="">All Categories</option>
              {categories.sort().map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Active filters */}
        {(search || selectedCategory) && (
          <div className="mt-3 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <span>Showing {filteredChannels.length} channels</span>
            {(search || selectedCategory) && (
              <button
                onClick={() => {
                  setSearch('')
                  setSelectedCategory('')
                }}
                className="text-blue-600 hover:text-blue-700"
              >
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
          {Array.from({ length: 20 }).map((_, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 animate-pulse">
              <div className="w-full h-24 bg-gray-200 dark:bg-gray-700 rounded mb-3" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2" />
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-3" />
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
          ))}
        </div>
      )}

      {/* Channels by Category */}
      {!isLoading && sortedCategories.length > 0 && (
        <div className="space-y-8">
          {sortedCategories.map((category) => (
            <div key={category}>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <span>{category}</span>
                <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                  ({groupedByCategory[category].length})
                </span>
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
                {groupedByCategory[category].map((channel) => (
                  <ChannelCard
                    key={channel.id}
                    channel={channel}
                    isPending={pendingAction === channel.id}
                    onAdd={() => addMutation.mutate(channel.id)}
                    onRemove={() => removeMutation.mutate(channel.id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No Results */}
      {!isLoading && filteredChannels.length === 0 && (
        <div className="text-center py-12">
          <Tv className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No channels found</h3>
          <p className="text-gray-500 dark:text-gray-400">
            {search || selectedCategory
              ? 'Try adjusting your search or filter'
              : 'Pluto TV channels could not be loaded'}
          </p>
        </div>
      )}
    </div>
  )
}

interface ChannelCardProps {
  channel: PlutoTVChannel
  isPending: boolean
  onAdd: () => void
  onRemove: () => void
}

function ChannelCard({ channel, isPending, onAdd, onRemove }: ChannelCardProps) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-md transition-shadow overflow-hidden">
      {/* Logo */}
      <div className="h-24 bg-gray-100 dark:bg-gray-900 flex items-center justify-center p-4">
        {channel.logo ? (
          <img
            src={channel.logo}
            alt={channel.name}
            className="max-h-full max-w-full object-contain"
            loading="lazy"
            onError={(e) => {
              ;(e.target as HTMLImageElement).style.display = 'none'
            }}
          />
        ) : (
          <Tv className="w-12 h-12 text-gray-400" />
        )}
      </div>

      {/* Content */}
      <div className="p-3">
        <h3 className="font-medium text-gray-900 dark:text-white truncate" title={channel.name}>
          {channel.name}
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 truncate mb-3">{channel.category}</p>

        {/* Add/Remove Button */}
        {channel.added ? (
          <button
            onClick={onRemove}
            disabled={isPending}
            className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-900/30 dark:hover:text-red-400 transition-colors group disabled:opacity-50"
          >
            <Check className="w-4 h-4 group-hover:hidden" />
            <X className="w-4 h-4 hidden group-hover:block" />
            <span className="group-hover:hidden">Added</span>
            <span className="hidden group-hover:inline">Remove</span>
          </button>
        ) : (
          <button
            onClick={onAdd}
            disabled={isPending}
            className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {isPending ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            <span>{isPending ? 'Adding...' : 'Add to OBA'}</span>
          </button>
        )}
      </div>
    </div>
  )
}
